import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { deskApi } from '@/api/desk.api';
import { showApiError } from '@/utils/apiError';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { notify } from '@/core/toast/notify';
import type { DeskCannedResponse } from '@/types/desk';

const emptyForm = { title: '', shortcutCode: '', bodyText: '', categoryCode: '' };

export function DeskCannedResponsesPage() {
  const { confirm, dialog } = useConfirm();
  const [items, setItems] = useState<DeskCannedResponse[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const { rows: paginatedItems, tablePaginationProps: itemsPaginationProps } = useClientPaginatedRows(items);

  const load = useCallback(() => {
    deskApi.listCannedResponses().then(setItems).catch((err) => showApiError(err, 'Failed to load canned responses'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.bodyText.trim()) return;
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        shortcutCode: form.shortcutCode.trim() || undefined,
        bodyText: form.bodyText,
        categoryCode: form.categoryCode.trim() || undefined,
      };
      if (editingId != null) {
        await deskApi.updateCannedResponse(editingId, payload);
        notify.success('Canned response updated');
      } else {
        await deskApi.createCannedResponse(payload);
        notify.success('Canned response created');
      }
      resetForm();
      load();
    } catch (err) {
      showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item: DeskCannedResponse) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      shortcutCode: item.shortcutCode ?? '',
      bodyText: item.bodyText,
      categoryCode: item.categoryCode ?? '',
    });
  };

  const remove = async (id: number) => {
    const ok = await confirm({
      title: 'Delete canned response',
      message: 'This snippet will be removed for all agents.',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deskApi.deleteCannedResponse(id);
      notify.success('Deleted');
      if (editingId === id) resetForm();
      load();
    } catch (err) {
      showApiError(err, 'Delete failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Canned responses"
        description="Reusable reply snippets for agents"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Desk', to: '/desk' },
          { label: 'Canned responses' },
        ]}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <form onSubmit={submit} className="card p-4 space-y-3">
          <h3 className="font-semibold">{editingId != null ? 'Edit response' : 'New response'}</h3>
          <Input label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Input
            label="Shortcut code"
            value={form.shortcutCode}
            onChange={(e) => setForm({ ...form, shortcutCode: e.target.value })}
          />
          <Input
            label="Category code"
            value={form.categoryCode}
            onChange={(e) => setForm({ ...form, categoryCode: e.target.value })}
          />
          <Textarea
            label="Body"
            className="min-h-[8rem]"
            value={form.bodyText}
            onChange={(e) => setForm({ ...form, bodyText: e.target.value })}
          />
          <div className="flex gap-2">
            <Button type="submit" loading={saving}>{editingId != null ? 'Update' : 'Create'}</Button>
            {editingId != null && (
              <Button type="button" variant="secondary" onClick={resetForm}>Cancel</Button>
            )}
          </div>
        </form>

        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3">Title</th>
                <th className="text-left p-3">Shortcut</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={3} className="p-6 text-slate-500">No canned responses yet</td>
                </tr>
              ) : (
                paginatedItems.map((item) => (
                  <tr key={item.id} className="border-t">
                    <td className="p-3">
                      <div className="font-medium">{item.title}</div>
                      <div className="text-xs text-slate-500 truncate max-w-xs">{item.bodyText}</div>
                    </td>
                    <td className="p-3">{item.shortcutCode || '—'}</td>
                    <td className="p-3 text-right space-x-2">
                      <Button variant="secondary" onClick={() => startEdit(item)}>Edit</Button>
                      <Button variant="secondary" onClick={() => void remove(item.id)}>Delete</Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <TablePagination {...itemsPaginationProps} hasRows={items.length > 0} />
        </div>
      </div>
      {dialog}
    </div>
  );
}
